import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {SortHeaderFilter} from '@pb/kit-component-filters';


import {COLUMNS, HEADERS} from '../../constants';
import {notificationsListStateActions, notificationsListStateSelector} from '../../slices/notification-search-criteria.slice';

import {NotificationAdminProfileFilter} from './notification-admin-profile.filter';

import type {ProfileRecord} from 'src/openapi/admin/models/ProfileRecord';

type Props = {
    roles: Array<ProfileRecord>,
};

export const NotificationAdminProfileHeader = ({roles}: Props) => {
    const dispatch = useDispatch();
    const state = useSelector(notificationsListStateSelector);
    const storedRoleIds = useMemo(() => (
        state.filters.adminProfileIds as (number[] | undefined) || []
    ), [state.filters.adminProfileIds]);

    const [selectedRoleIds, setSelectedRolesIds] = useState<number[]>(storedRoleIds);

    useEffect(() => {
        setSelectedRolesIds(storedRoleIds);
    }, [storedRoleIds]);

    const applyFilter = useCallback(() => {
        if (!isDeepEqual({prev: storedRoleIds, current: selectedRoleIds})) {
            const {adminProfileIds: _adminProfileIds, ...filters} = state.filters;

            dispatch(notificationsListStateActions.setFilters({
                ...filters,
                ...selectedRoleIds.length ? {adminProfileIds: selectedRoleIds} : {},
            }));
        }
    }, [selectedRoleIds, storedRoleIds, dispatch, state.filters]);

    const clearFilter = useCallback(() => {
        const {adminProfileIds: _adminProfileIds, ...filters} = state.filters;
        setSelectedRolesIds([]);
        dispatch(notificationsListStateActions.setFilters(filters));
    }, [state, setSelectedRolesIds, dispatch]);

    const cancelFilter = useCallback(() => {
        setSelectedRolesIds(storedRoleIds);
    }, [storedRoleIds, setSelectedRolesIds]);

    return (
        <SortHeaderFilter
            filterActive={!!storedRoleIds.length}
            sortable={false}
            caption={HEADERS[COLUMNS.ADMIN_PROFILES]}
            direction={undefined}
            defaultDirection={SortDirections.ASC}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <NotificationAdminProfileFilter
                selectedRoles={selectedRoleIds}
                roles={roles}
                onChange={setSelectedRolesIds}
            />
        </SortHeaderFilter>
    );
};
